import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ObjectId } from 'mongodb';
import { UserUpdatePayload, ExtendedSafeDatabaseUser } from '../types/types';
import { getUserByUsername, followUser, unfollowUser } from '../services/userService';
import useUserContext from './useUserContext';

/**
 * Extended type that includes the optional fields we're using
 */
interface ExtendedUserData extends ExtendedSafeDatabaseUser {
  _id: ObjectId;
  username: string;
  dateJoined: Date;
  biography?: string;
  isAdmin?: boolean;
  followers?: Map<string, Date>;
  following?: Map<string, Date>;
  rank?: number;
  profilePicture?: string;
  visibility?: string;
}

/**
 * Custom hook to manage state and logic for the ProfilePage component
 * @returns Object containing profile page state and functions
 */
const useProfilePage = () => {
  const { username } = useParams<{ username: string }>();
  const { user: currentUser, socket } = useUserContext();

  const [userData, setUserData] = useState<ExtendedUserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [friendshipDuration, setFriendshipDuration] = useState<string | null>(null);

  const isOwnProfile = userData?.username === currentUser.username;
  const followerCount = userData?.followers ? userData.followers.size : 0;
  const followingCount = userData?.following ? userData.following.size : 0;

  /**
   * Calculate the friendship duration from a date
   */
  const calculateFriendshipDuration = (date: Date): string => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'since today';
    }
    if (diffDays === 1) {
      return 'since yesterday';
    }
    if (diffDays < 30) {
      return `for ${diffDays} days`;
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `for ${months} ${months === 1 ? 'month' : 'months'}`;
    }

    const years = Math.floor(diffDays / 365);
    const remainingMonths = Math.floor((diffDays % 365) / 30);

    if (remainingMonths === 0) {
      return `for ${years} ${years === 1 ? 'year' : 'years'}`;
    }

    return `for ${years} ${years === 1 ? 'year' : 'years'} and ${remainingMonths} ${remainingMonths === 1 ? 'month' : 'months'}`;
  };

  // Fetch user data on component mount or username change
  useEffect(() => {
    const fetchUserData = async () => {
      if (!username) return;

      try {
        setLoading(true);
        setError(null);
        const data = (await getUserByUsername(username)) as ExtendedUserData;

        // Process warnings data if it exists
        if (data.warnings && Array.isArray(data.warnings)) {
          data.warnings = data.warnings.map(warning => ({
            ...warning,
            date: new Date(warning.date),
          }));
        }

        // Convert followers and following from objects to Maps bc they're serialized from the server
        if (data.followers && !(data.followers instanceof Map)) {
          data.followers = new Map(
            Object.entries(data.followers).map(([key, value]) => [key, new Date(value as string)]),
          );
        }

        if (data.following && !(data.following instanceof Map)) {
          data.following = new Map(
            Object.entries(data.following).map(([key, value]) => [key, new Date(value as string)]),
          );
        }

        setUserData(data);

        if (currentUser.username && data.followers) {
          setIsFollowing(data.followers.has(currentUser.username));
        }

        // Check if they follow each other for friendship duration
        if (
          currentUser.username &&
          data.followers?.has(currentUser.username) &&
          data.following?.has(currentUser.username)
        ) {
          // Find the earliest date when they started following each other
          const followDate = data.followers.get(currentUser.username);
          const followBackDate = data.following.get(currentUser.username);

          if (followDate && followBackDate) {
            const earliestDate = followDate < followBackDate ? followDate : followBackDate;
            setFriendshipDuration(calculateFriendshipDuration(earliestDate));
          } else {
            setFriendshipDuration(null);
          }
        } else {
          setFriendshipDuration(null);
        }
      } catch (err) {
        if (username?.startsWith('Guest_')) {
          setError('Guest users do not have profiles.');
        } else {
          setError('Error fetching user profile');
        }
        setUserData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [username, currentUser.username]);

  useEffect(() => {
    if (!username || !socket) {
      return undefined;
    }

    const handleUserUpdate = (payload: UserUpdatePayload) => {
      if (payload.user.username === username) {
        const updatedUser = payload.user as ExtendedUserData;

        // Convert followers and following from objects to Maps
        if (updatedUser.followers && !(updatedUser.followers instanceof Map)) {
          updatedUser.followers = new Map(
            Object.entries(updatedUser.followers).map(([key, value]) => [
              key,
              new Date(value as string),
            ]),
          );
        }

        if (updatedUser.following && !(updatedUser.following instanceof Map)) {
          updatedUser.following = new Map(
            Object.entries(updatedUser.following).map(([key, value]) => [
              key,
              new Date(value as string),
            ]),
          );
        }

        setUserData(updatedUser);

        if (currentUser.username && updatedUser.followers) {
          setIsFollowing(updatedUser.followers.has(currentUser.username));
        }

        // Update friendship duration if they follow each other
        if (
          currentUser.username &&
          updatedUser.followers?.has(currentUser.username) &&
          updatedUser.following?.has(currentUser.username)
        ) {
          const followDate = updatedUser.followers.get(currentUser.username);
          const followBackDate = updatedUser.following.get(currentUser.username);

          if (followDate && followBackDate) {
            const earliestDate = followDate < followBackDate ? followDate : followBackDate;
            setFriendshipDuration(calculateFriendshipDuration(earliestDate));
          } else {
            setFriendshipDuration(null);
          }
        } else {
          setFriendshipDuration(null);
        }
      }
    };

    socket.on('userUpdate', handleUserUpdate);

    return () => {
      socket.off('userUpdate', handleUserUpdate);
    };
  }, [socket, username, currentUser.username]);

  /**
   * Handle following a user
   */
  const handleFollow = async () => {
    if (!currentUser.username || !username || isOwnProfile) return;

    try {
      setLoading(true);
      await followUser(currentUser.username, username);
      setIsFollowing(true);
      setSuccessMessage(`Successfully followed ${username}`);

      const updatedUser = (await getUserByUsername(username)) as ExtendedUserData;

      // Convert followers and following from objects to Maps
      if (updatedUser.followers && !(updatedUser.followers instanceof Map)) {
        updatedUser.followers = new Map(
          Object.entries(updatedUser.followers).map(([key, value]) => [
            key,
            new Date(value as string),
          ]),
        );
      }

      if (updatedUser.following && !(updatedUser.following instanceof Map)) {
        updatedUser.following = new Map(
          Object.entries(updatedUser.following).map(([key, value]) => [
            key,
            new Date(value as string),
          ]),
        );
      }

      setUserData(updatedUser);

      // Check if mutual following exists after this follow action
      if (updatedUser.following?.has(currentUser.username)) {
        const followDate = updatedUser.followers?.get(currentUser.username);
        const followBackDate = updatedUser.following.get(currentUser.username);

        if (followDate && followBackDate) {
          const earliestDate = followDate < followBackDate ? followDate : followBackDate;
          setFriendshipDuration(calculateFriendshipDuration(earliestDate));
        }
      }
    } catch (err) {
      setError('Failed to follow user');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle unfollowing a user
   */
  const handleUnfollow = async () => {
    if (!currentUser.username || !username || isOwnProfile) return;

    try {
      setLoading(true);
      await unfollowUser(currentUser.username, username);
      setIsFollowing(false);
      setSuccessMessage(`Successfully unfollowed ${username}`);
      setFriendshipDuration(null);

      const updatedUser = (await getUserByUsername(username)) as ExtendedUserData;

      // Convert followers and following from objects to Maps
      if (updatedUser.followers && !(updatedUser.followers instanceof Map)) {
        updatedUser.followers = new Map(
          Object.entries(updatedUser.followers).map(([key, value]) => [
            key,
            new Date(value as string),
          ]),
        );
      }

      if (updatedUser.following && !(updatedUser.following instanceof Map)) {
        updatedUser.following = new Map(
          Object.entries(updatedUser.following).map(([key, value]) => [
            key,
            new Date(value as string),
          ]),
        );
      }

      setUserData(updatedUser);
    } catch (err) {
      setError('Failed to unfollow user');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear any success or error messages
   */
  const clearMessages = () => {
    setSuccessMessage(null);
    setError(null);
  };

  return {
    userData,
    loading,
    error,
    isFollowing,
    isOwnProfile,
    followerCount,
    followingCount,
    friendshipDuration,
    successMessage,
    handleFollow,
    handleUnfollow,
    clearMessages,
    currentUser,
  };
};

export default useProfilePage;
